import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CommonItem, GameState, Investigator, Monster, UserAction, UserLanguage } from '../api';
import { ActionsList } from './ActionsList';
import { Card } from './Card';
import { InvestigatorCompactStatus } from './GameStatusSummary';
import { InvestigatorInspector } from './InvestigatorInspector';
import { MonsterInspector, MonsterInspectionEnabled } from './MonsterInspector';
import { MonsterDetailCard } from './Monsters';
import { MovementActionIcon } from './MovementActionIcon';
import { fightControlKind, fightEquipment, monsterChoice, remainingFightMonsters } from './fightPresentation';
import { SpellInvestigatorContext } from './SpellCastingContext';
import './FightActions.css';
import { useMonsterTokenStyle } from '../utils/clientSettings';

type Props = { state: GameState; investigator: Investigator; actions: UserAction[]; loading: boolean;
  language?: UserLanguage; viewerName: string; controlsInline: boolean; onExecuteAction: (id: string) => void };

export function FightActionControls({ actions, state, loading, onExecuteAction }: Pick<Props, 'actions' | 'state' | 'loading' | 'onExecuteAction'>) {
  const controls = actions.filter(action => !!fightControlKind(action, state.currentFightingMonster));
  if (!controls.length) return null;
  return <div className="fight-action-controls" role="group" aria-label="Fight controls">
    {controls.map(action => {
      const kind = fightControlKind(action, state.currentFightingMonster)!;
      return <button type="button" key={action.id} className={`fight-action-control fight-action-${kind}`}
        disabled={loading || !!action.disabled} title={action.description} onClick={() => { if (!loading && !action.disabled) onExecuteAction(action.id); }}>
        {kind === 'undo' || kind === 'continue' ? <MovementActionIcon action={action} completion={kind === 'continue'} /> : <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d={kind === 'fight' ? 'm5 3 14 14m-3 3 5-5M3 5l14 14m-1-3 4 4M8 3l-5 5M3 3l5 5' : 'M4 12h15m-6-6 6 6-6 6'} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>}
        <span>{action.name}</span>
      </button>;
    })}
  </div>;
}

export function FightActions(props: Props) {
  const monsterTokenStyle = useMonsterTokenStyle();
  const { state, investigator, actions, loading, language, viewerName, controlsInline, onExecuteAction } = props;
  const [inspect, setInspect] = useState(false);
  const [monsterDetails, setMonsterDetails] = useState<Monster | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const choices = actions.filter(action => !!monsterChoice(action));
  const current = state.currentFightingMonster;
  const latestMonsterDetails = monsterDetails && [current, ...(state.currentCombatMonsters ?? []), ...choices.map(monsterChoice)]
    .find(value => value && (monsterDetails.id ? value.id === monsterDetails.id : value.name === monsterDetails.name));
  useEffect(() => { if (monsterDetails && !latestMonsterDetails) setMonsterDetails(null); }, [monsterDetails, latestMonsterDetails]);
  const scene = JSON.stringify([state.gameId, investigator.name, choices.length ? 'selection' : current?.name, state.currentCombatMonsters?.length]);
  useLayoutEffect(() => {
    const scroller = root.current?.closest('.game-fullscreen-actions-body');
    if (scroller) scroller.scrollTop = 0;
  }, [scene]);
  const equipment = fightEquipment(investigator, actions);
  const represented = new Set([...choices, ...equipment.flatMap(item => item.action ? [item.action] : []),
    ...actions.filter(action => !!fightControlKind(action, current))]);
  const remaining = actions.filter(action => !represented.has(action));
  const execute = (action: UserAction) => { if (!loading && !action.disabled) onExecuteAction(action.id); };
  return <div ref={root} className="fight-actions" aria-label={`${investigator.name} fight`}>
    {choices.length > 0 ? <div className="fight-monster-choices" role="group" aria-label="Choose a monster">
      {choices.map(action => <div className="fight-monster-choice" key={action.id}>
        <button type="button" className="fight-monster-select" aria-label={action.description || action.name}
          title={action.description} disabled={loading || !!action.disabled} onClick={() => execute(action)}>
          <span className="fight-monster-preview" aria-hidden="true" inert><MonsterInspectionEnabled.Provider value={false}><MonsterDetailCard monster={monsterChoice(action)!} /></MonsterInspectionEnabled.Provider></span>
        </button>
        <button type="button" className="fight-monster-details monster-info-open" aria-label={`Details for ${monsterChoice(action)!.name}`} onClick={() => setMonsterDetails(monsterChoice(action)!)}>i</button>
      </div>)}
    </div> : current && <>
      <div className="fight-remaining-monsters" role="group" aria-label="Remaining monsters">
        {remainingFightMonsters(state.currentCombatMonsters ?? [], current).map((monster, index) =>
          <button type="button" key={`${index}-${monster.name}`} className="fight-remaining-monster" aria-label={`Inspect ${monster.name}`} onClick={() => setMonsterDetails(monster)}>
            <span className="fight-monster-preview" aria-hidden="true" inert><MonsterInspectionEnabled.Provider value={false}><MonsterDetailCard monster={monster} /></MonsterInspectionEnabled.Provider></span>
          </button>)}
      </div>
      <div className="fight-current-monster" aria-label={`Current monster: ${current.name}`}><MonsterDetailCard monster={current} /></div>
      {!!state.dunwichHorrorCurrentRollInfo && current.name === 'The Dunwich Horror' && monsterTokenStyle !== 'Digital' && <img className="fight-special-monster-roll" alt={state.dunwichHorrorCurrentRollInfo} src={`/Monsters/${state.dunwichHorrorCurrentRollInfo}.png`} />}
      <div className="fight-versus" aria-hidden="true">VS</div>
    </>}
    <div className="fight-investigator-status" role="button" tabIndex={0} aria-haspopup="dialog" aria-label={`View ${investigator.name} details`}
      onClick={() => setInspect(true)} onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setInspect(true); }
      }}>
      <InvestigatorCompactStatus investigator={investigator} showMoney={false} />
    </div>
    {equipment.length > 0 && <SpellInvestigatorContext.Provider value={investigator.name}>
      <div className="fight-equipment" role="group" aria-label="Equipment">
        {equipment.map(({ entry, action }, index) => {
          const item = entry.item as CommonItem;
          const used = !!(item.used || item.exhausted);
          const label = item.title?.trim() || item.name;
          const available = !!action && !action.disabled && !loading;
          return <div key={`${entry.kind}-${item.id ?? index}`} className={`fight-equipment-card${item.equipped ? ' is-equipped' : ''}${used ? ' is-used' : ''}`}>
            <div className={`fight-equipment-body${available ? ' is-actionable' : ''}`} onClick={event => {
              if (!event.currentTarget.contains(event.target as Node)) return;
              if ((event.target as Element).closest('button, a, input, select, textarea, summary')) return;
              if (action) execute(action);
            }}><Card card={entry} /></div>
            <div className="fight-equipment-caption">
              {item.equipped && <span className="fight-equipped-marker">✓ Equipped</span>}
              {used && <span className="fight-used-marker">Used</span>}
              <button type="button" className="fight-equipment-toggle" aria-label={`${item.equipped ? 'Unequip' : 'Equip'} ${label}`}
                aria-pressed={!!item.equipped} disabled={!available} title={action?.description || 'Equipment cannot be changed now'}
                onClick={() => { if (action) execute(action); }}>{item.equipped ? 'Unequip' : 'Equip'}</button>
            </div>
            {action?.disabled && <small className="fight-equipment-restriction">{action.description}</small>}
          </div>;
        })}
      </div>
    </SpellInvestigatorContext.Provider>}
    {remaining.length > 0 && <ActionsList compact actions={remaining} loading={loading} language={language} onExecuteAction={onExecuteAction} />}
    {controlsInline && <FightActionControls {...props} />}
    {inspect && <InvestigatorInspector key={`${state.gameId}:${investigator.name}`} state={state} investigator={investigator} investigators={[investigator]} viewerName={viewerName}
      actions={actions} loading={loading} language={language} onExecuteAction={onExecuteAction} onClose={() => setInspect(false)} />}
    {latestMonsterDetails && <MonsterInspector monster={latestMonsterDetails} onClose={() => setMonsterDetails(null)} />}
  </div>;
}
